import { useState, useRef } from 'react';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import EmailInputForm from './login/EmailInputForm';
import ProviderSelection from './login/ProviderSelection';
import FormHeader from './login/FormHeader';

interface LoginFormProps {
  onSuccess: (email: string) => void;
}

const LoginForm = ({ onSuccess }: LoginFormProps) => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showProviders, setShowProviders] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Enhanced validation
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setError('');
    // Show provider options instead of immediately submitting
    setShowProviders(true);
  };
  
  const handleProviderConnect = (provider: string) => {
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      onSuccess(email);
    }, 1500);
  };
  
  // Determine email domain for provider suggestion
  const emailDomain = email.split('@')[1]?.toLowerCase() || '';
  const suggestedProvider = 
    emailDomain.includes('gmail') ? 'Google' :
    emailDomain.includes('outlook') || emailDomain.includes('hotmail') || emailDomain.includes('live') ? 'Microsoft' :
    'Email';
  
  return (
    <motion.div
      key="form"
      layout
      layoutId="login-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        layout: { 
          type: "spring", 
          stiffness: 200, 
          damping: 20,
          mass: 1.2
        },
        duration: 0.6
      }}
      className="w-full max-w-md mx-auto bg-white rounded-2xl shadow-lg overflow-hidden"
    >
      {/* Header section */}
      <FormHeader showProviders={showProviders} />
      
      <LayoutGroup id="login-form-layout">
        <AnimatePresence mode="wait">
          {!showProviders ? (
            <EmailInputForm 
              email={email}
              setEmail={setEmail}
              error={error}
              setError={setError}
              isSubmitting={isSubmitting}
              inputRef={inputRef}
              onSubmit={handleSubmit}
            />
          ) : (
            <ProviderSelection 
              email={email}
              suggestedProvider={suggestedProvider}
              isSubmitting={isSubmitting}
              error={error}
              setError={setError}
              onProviderConnect={handleProviderConnect}
              onBack={() => setShowProviders(false)}
            />
          )}
        </AnimatePresence>
      </LayoutGroup>
    </motion.div>
  );
};

export default LoginForm;